// ============================================================================
// KLINISOL CRO - Personnel Qualification Dashboard
// File: src/pages/PersonnelDirectory.js
// FDA 21 CFR Part 11 / ICH-GCP E6(R3) Compliant
// Version: 1.0
// Date: December 3, 2025
// ============================================================================

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Users,
    User,
    AlertTriangle,
    CheckCircle,
    Clock,
    XCircle,
    Search,
    Filter,
    FileText,
    Award,
    Upload,
    Eye,
    ChevronRight,
    ArrowLeft,
    HelpCircle,
    X,
    Shield,
    Briefcase,
    Calendar,
    AlertCircle,
    RefreshCw
} from 'lucide-react';
import personnelApi from '../api/personnelApi';
import { useAuth } from '../context/AuthContext';

// ============================================================================
// MAIN COMPONENT
// ============================================================================
const PersonnelDirectory = () => {
    const navigate = useNavigate();
    const { user: currentUser } = useAuth();
    
    // State
    const [directory, setDirectory] = useState([]);
    const [complianceSummary, setComplianceSummary] = useState(null);
    const [expiringCredentials, setExpiringCredentials] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [accessLevel, setAccessLevel] = useState('self');
    const [searchTerm, setSearchTerm] = useState('');
    const [filterStatus, setFilterStatus] = useState('all');
    const [activeTab, setActiveTab] = useState('directory');
    const [showHelp, setShowHelp] = useState(false);

    // ========================================================================
    // DATA LOADING
    // ========================================================================
    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        setLoading(true);
        setError(null);
        try {
            const [directoryRes, complianceRes, expiringRes] = await Promise.all([
                personnelApi.getDirectory(),
                personnelApi.getComplianceSummary().catch(() => null),
                personnelApi.getExpiringCredentials(90).catch(() => null)
            ]);

            setDirectory(directoryRes.data || []);
            setAccessLevel(directoryRes.accessLevel || 'self');
            
            if (complianceRes) {
                setComplianceSummary(complianceRes);
            }
            if (expiringRes) {
                setExpiringCredentials(expiringRes.data || []);
            }
        } catch (err) {
            console.error('Error loading personnel data:', err);
            setError('Failed to load personnel data');
        } finally {
            setLoading(false);
        }
    };

    // ========================================================================
    // FILTERING
    // ========================================================================
    const filteredDirectory = directory.filter(person => {
        const matchesSearch = 
            person.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            person.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            person.job_title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            person.department?.toLowerCase().includes(searchTerm.toLowerCase());

        const matchesFilter = filterStatus === 'all' || 
            (filterStatus === 'qualified' && person.qualification_status === 'qualified') ||
            (filterStatus === 'incomplete' && person.qualification_status !== 'qualified') ||
            (filterStatus === 'expiring' && person.expiring_credentials > 0);

        return matchesSearch && matchesFilter;
    });

    // ========================================================================
    // COMPLIANCE SCORE COLOR
    // ========================================================================
    const getScoreColor = (score) => {
        if (score >= 100) return 'text-green-600 bg-green-100';
        if (score >= 75) return 'text-yellow-600 bg-yellow-100';
        if (score >= 50) return 'text-orange-600 bg-orange-100';
        return 'text-red-600 bg-red-100';
    };

    const getStatusBadge = (status) => {
        switch (status) {
            case 'qualified':
                return <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">Qualified</span>;
            case 'pending_review':
                return <span className="px-2 py-1 text-xs font-medium rounded-full bg-yellow-100 text-yellow-800">Pending Review</span>;
            case 'expired':
                return <span className="px-2 py-1 text-xs font-medium rounded-full bg-red-100 text-red-800">Expired</span>;
            default:
                return <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-800">Incomplete</span>;
        }
    };

    // ========================================================================
    // HELP MODAL
    // ========================================================================
    const HelpModal = () => (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden">
                {/* Header */}
                <div className="bg-gradient-to-r from-[#475569] to-[#334155] text-white p-6">
                    <div className="flex justify-between items-start">
                        <div className="flex items-center gap-3">
                            <Users className="w-8 h-8" />
                            <div>
                                <h2 className="text-xl font-bold">Personnel Qualification Module</h2>
                                <p className="text-sm opacity-90">Understanding the Staff Directory</p>
                            </div>
                        </div>
                        <button onClick={() => setShowHelp(false)} className="hover:bg-white/20 p-1 rounded">
                            <X className="w-6 h-6" />
                        </button>
                    </div>
                </div>

                {/* Content */}
                <div className="p-6 space-y-6 overflow-y-auto max-h-[calc(90vh-200px)]">
                    
                    {/* Governing Document */}
                    <section className="bg-slate-100 border-l-4 border-[#475569] rounded-r-lg p-4">
                        <h4 className="font-bold text-slate-900 mb-2 flex items-center gap-2">
                            <FileText className="w-4 h-4" />
                            <span>Governing Document</span>
                        </h4>
                        <p className="text-sm text-slate-700">
                            Personnel qualification records are maintained per <strong>QMS-002 v1.1: Training and Competency Management</strong>. 
                            All staff must maintain current qualifications aligned with ICH E6(R3) Section 2.8 requirements.
                        </p>
                    </section>

                    {/* Required Documents */}
                    <section>
                        <h4 className="font-bold text-gray-900 text-lg mb-3">Required Documents</h4>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                                <div className="flex items-center gap-2 mb-2">
                                    <FileText className="w-5 h-5 text-blue-600" />
                                    <span className="font-semibold text-blue-900">CV / Resume</span>
                                </div>
                                <p className="text-xs text-blue-700">Current, dated, demonstrates qualifications</p>
                            </div>
                            <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                                <div className="flex items-center gap-2 mb-2">
                                    <Briefcase className="w-5 h-5 text-blue-600" />
                                    <span className="font-semibold text-blue-900">Job Description</span>
                                </div>
                                <p className="text-xs text-blue-700">Defines role responsibilities</p>
                            </div>
                            <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                                <div className="flex items-center gap-2 mb-2">
                                    <Shield className="w-5 h-5 text-blue-600" />
                                    <span className="font-semibold text-blue-900">Confidentiality Agreement</span>
                                </div>
                                <p className="text-xs text-blue-700">Data protection commitment</p>
                            </div>
                        </div>
                    </section>

                    {/* Qualification Status */}
                    <section>
                        <h4 className="font-bold text-gray-900 text-lg mb-3">Qualification Status</h4>
                        <div className="space-y-2">
                            <div className="flex items-center gap-3 p-2 rounded bg-green-50">
                                <CheckCircle className="w-5 h-5 text-green-600" />
                                <div>
                                    <span className="font-medium text-green-900">Qualified</span>
                                    <span className="text-sm text-green-700 ml-2">All required documents current and approved</span>
                                </div>
                            </div>
                            <div className="flex items-center gap-3 p-2 rounded bg-yellow-50">
                                <Clock className="w-5 h-5 text-yellow-600" />
                                <div>
                                    <span className="font-medium text-yellow-900">Pending Review</span>
                                    <span className="text-sm text-yellow-700 ml-2">Documents uploaded, awaiting approval</span>
                                </div>
                            </div>
                            <div className="flex items-center gap-3 p-2 rounded bg-gray-50">
                                <AlertCircle className="w-5 h-5 text-gray-600" />
                                <div>
                                    <span className="font-medium text-gray-900">Incomplete</span>
                                    <span className="text-sm text-gray-700 ml-2">Missing required documents</span>
                                </div>
                            </div>
                            <div className="flex items-center gap-3 p-2 rounded bg-red-50">
                                <XCircle className="w-5 h-5 text-red-600" />
                                <div>
                                    <span className="font-medium text-red-900">Expired</span>
                                    <span className="text-sm text-red-700 ml-2">Credentials or documents have expired</span>
                                </div>
                            </div>
                        </div>
                    </section>

                    {/* Compliance Score */}
                    <section>
                        <h4 className="font-bold text-gray-900 text-lg mb-3">Compliance Score</h4>
                        <p className="text-gray-700 mb-3">
                            The compliance score is calculated based on four key components, each worth 25%:
                        </p>
                        <div className="grid grid-cols-2 gap-2 text-sm">
                            <div className="p-2 bg-gray-50 rounded">✓ CV / Resume</div>
                            <div className="p-2 bg-gray-50 rounded">✓ Job Description</div>
                            <div className="p-2 bg-gray-50 rounded">✓ Confidentiality Agreement</div>
                            <div className="p-2 bg-gray-50 rounded">✓ Current GCP Certificate</div>
                        </div>
                    </section>

                    {/* Access Levels */}
                    <section className="bg-amber-50 border-l-4 border-amber-500 rounded-r-lg p-4">
                        <h4 className="font-bold text-amber-900 mb-2 flex items-center gap-2">
                            <Shield className="w-4 h-4" />
                            <span>Access Control</span>
                        </h4>
                        <div className="text-sm text-amber-800 space-y-1">
                            <p><strong>Admin (CRL):</strong> Full access - view, edit, approve all records</p>
                            <p><strong>Auditor (QAC):</strong> Read-only access to all records and reports</p>
                            <p><strong>Manager (PM):</strong> Limited view - basic profile info only</p>
                            <p><strong>Self:</strong> View and upload own documents only</p>
                        </div>
                    </section>

                    {/* Questions */}
                    <section className="bg-[#E91E63]/10 border-l-4 border-[#E91E63] rounded-r-lg p-4">
                        <h4 className="font-bold text-gray-900 mb-2">Questions or Concerns?</h4>
                        <div className="text-sm text-gray-700 space-y-2">
                            <p><strong>Qualification updates:</strong> Contact the <strong>Clinical & Regulatory Lead (CRL)</strong></p>
                            <p><strong>Document uploads:</strong> Contact the <strong>Project Manager (PM)</strong></p>
                            <p><strong>Compliance audits:</strong> Contact the <strong>Quality Assurance Coordinator (QAC)</strong></p>
                        </div>
                    </section>

                    {/* Footer */}
                    <section className="text-center text-sm text-gray-500 pt-4 border-t">
                        <p>Governed by: <strong>QMS-002 v1.1</strong> Training and Competency Management</p>
                        <p className="mt-1">KLINISOL CRO Quality Management System</p>
                    </section>
                </div>
            </div>
        </div>
    );

    // ========================================================================
    // RENDER
    // ========================================================================
    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <RefreshCw className="w-8 h-8 animate-spin text-[#475569] mx-auto mb-4" />
                    <p className="text-gray-600">Loading personnel data...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Help Modal */}
            {showHelp && <HelpModal />}

            {/* Header */}
            <header className="bg-gradient-to-r from-[#475569] via-[#E91E63] to-[#475569] text-white">
                <div className="max-w-7xl mx-auto px-4 py-6">
                    <div className="flex justify-between items-start">
                        <div className="flex items-center gap-4">
                            <button 
                                onClick={() => navigate('/dashboard')}
                                className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                            >
                                <ArrowLeft className="w-5 h-5" />
                            </button>
                            <div className="flex items-center gap-3">
                                <Users className="w-8 h-8" />
                                <div>
                                    <h1 className="text-2xl font-bold">Personnel Qualifications</h1>
                                    <p className="text-sm opacity-90">Staff Directory & Compliance • QMS-002 v1.1</p>
                                </div>
                            </div>
                        </div>
                        <div className="flex items-center gap-3">
                            <button
                                onClick={() => navigate(`/personnel/${currentUser?.id}`)}
                                className="px-4 py-2 bg-white text-[#E91E63] hover:bg-gray-100 rounded-lg transition-colors flex items-center gap-2 font-medium"
                            >
                                <Upload className="w-4 h-4" />
                                Upload Document
                            </button>
                            <button
                                onClick={() => setShowHelp(true)}
                                className="px-4 py-2 border border-white/30 hover:bg-white/10 rounded-lg transition-colors flex items-center gap-2"
                            >
                                <HelpCircle className="w-4 h-4" />
                                Help
                            </button>
                        </div>
                    </div>
                </div>
            </header>

            {/* Tabs */}
            <div className="bg-white shadow">
                <div className="max-w-7xl mx-auto px-4">
                    <nav className="flex space-x-8">
                        {['directory', 'compliance', 'credentials'].map(tab => (
                            <button
                                key={tab}
                                onClick={() => setActiveTab(tab)}
                                className={`py-4 px-1 border-b-2 font-medium text-sm capitalize ${
                                    activeTab === tab
                                        ? 'border-[#E91E63] text-[#E91E63]'
                                        : 'border-transparent text-gray-500 hover:text-gray-700'
                                }`}
                            >
                                {tab === 'directory' && 'Staff Directory'}
                                {tab === 'compliance' && 'Compliance Summary'}
                                {tab === 'credentials' && 'Expiring Credentials'}
                            </button>
                        ))}
                    </nav>
                </div>
            </div>

            {/* Content */}
            <main className="max-w-7xl mx-auto px-4 py-6">
                
                {/* Error Display */}
                {error && (
                    <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
                        <AlertTriangle className="w-5 h-5 text-red-600" />
                        <span className="text-red-800">{error}</span>
                    </div>
                )}

                {/* Summary Cards - Only for Admin/Auditor */}
                {(accessLevel === 'admin' || accessLevel === 'auditor') && complianceSummary && (
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                        <div className="bg-white rounded-lg shadow p-4">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-gray-500">Total Staff</p>
                                    <p className="text-2xl font-bold text-gray-900">{complianceSummary.totals?.totalStaff || 0}</p>
                                </div>
                                <Users className="w-10 h-10 text-gray-300" />
                            </div>
                        </div>
                        <div className="bg-white rounded-lg shadow p-4">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-gray-500">Fully Compliant</p>
                                    <p className="text-2xl font-bold text-green-600">{complianceSummary.totals?.fullyCompliant || 0}</p>
                                </div>
                                <CheckCircle className="w-10 h-10 text-green-200" />
                            </div>
                        </div>
                        <div className="bg-white rounded-lg shadow p-4">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-gray-500">Expiring Credentials</p>
                                    <p className="text-2xl font-bold text-yellow-600">{expiringCredentials.length}</p>
                                </div>
                                <Clock className="w-10 h-10 text-yellow-200" />
                            </div>
                        </div>
                        <div className="bg-white rounded-lg shadow p-4">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-gray-500">Avg. Compliance</p>
                                    <p className="text-2xl font-bold text-blue-600">{complianceSummary.totals?.averageScore || 0}%</p>
                                </div>
                                <Award className="w-10 h-10 text-blue-200" />
                            </div>
                        </div>
                    </div>
                )}

                {/* Directory Tab */}
                {activeTab === 'directory' && (
                    <div className="bg-white rounded-lg shadow">
                        {/* Search & Filter */}
                        <div className="p-4 border-b flex flex-col md:flex-row gap-4">
                            <div className="relative flex-1">
                                <Search className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                                <input
                                    type="text"
                                    placeholder="Search by name, email, title, or department..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#E91E63] focus:border-transparent"
                                />
                            </div>
                            <div className="flex items-center gap-2">
                                <Filter className="w-5 h-5 text-gray-400" />
                                <select
                                    value={filterStatus}
                                    onChange={(e) => setFilterStatus(e.target.value)}
                                    className="border rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#E91E63]"
                                >
                                    <option value="all">All Staff</option>
                                    <option value="qualified">Qualified</option>
                                    <option value="incomplete">Incomplete</option>
                                    <option value="expiring">Expiring Credentials</option>
                                </select>
                            </div>
                        </div>

                        {/* Directory Table */}
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead className="bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    <tr>
                                        <th className="px-6 py-3">Staff Member</th>
                                        <th className="px-6 py-3">Role</th>
                                        <th className="px-6 py-3">Department</th>
                                        <th className="px-6 py-3">Status</th>
                                        {(accessLevel === 'admin' || accessLevel === 'auditor') && (
                                            <>
                                                <th className="px-6 py-3">Documents</th>
                                                <th className="px-6 py-3">Credentials</th>
                                            </>
                                        )}
                                        <th className="px-6 py-3">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200">
                                    {filteredDirectory.map(person => (
                                        <tr key={person.user_id} className="hover:bg-gray-50">
                                            <td className="px-6 py-4">
                                                <div className="flex items-center gap-3">
                                                    <div className="w-10 h-10 rounded-full bg-[#475569] flex items-center justify-center text-white font-medium">
                                                        {person.first_name?.[0]}{person.last_name?.[0]}
                                                    </div>
                                                    <div>
                                                        <p className="font-medium text-gray-900">{person.full_name}</p>
                                                        <p className="text-sm text-gray-500">{person.email}</p>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <div>
                                                    <p className="text-sm font-medium text-gray-900">{person.job_title || '-'}</p>
                                                    {person.functional_role_code && (
                                                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                                                            {person.functional_role_code}
                                                        </span>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 text-sm text-gray-500">
                                                {person.department || '-'}
                                            </td>
                                            <td className="px-6 py-4">
                                                {getStatusBadge(person.qualification_status)}
                                            </td>
                                            {(accessLevel === 'admin' || accessLevel === 'auditor') && (
                                                <>
                                                    <td className="px-6 py-4">
                                                        <div className="flex items-center gap-2">
                                                            <span className={`text-sm font-medium ${person.missing_required_docs > 0 ? 'text-red-600' : 'text-green-600'}`}>
                                                                {person.document_count || 0}
                                                            </span>
                                                            {person.missing_required_docs > 0 && (
                                                                <span className="text-xs text-red-500">
                                                                    ({person.missing_required_docs} missing)
                                                                </span>
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4">
                                                        <div className="flex items-center gap-2">
                                                            <span className="text-sm font-medium">{person.credential_count || 0}</span>
                                                            {person.expiring_credentials > 0 && (
                                                                <span className="text-xs text-yellow-600">
                                                                    ({person.expiring_credentials} expiring)
                                                                </span>
                                                            )}
                                                        </div>
                                                    </td>
                                                </>
                                            )}
                                            <td className="px-6 py-4">
                                                <button
                                                    onClick={() => navigate(`/personnel/${person.user_id}`)}
                                                    className="text-[#E91E63] hover:text-[#C2185B] font-medium text-sm flex items-center gap-1"
                                                >
                                                    View <ChevronRight className="w-4 h-4" />
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>

                            {filteredDirectory.length === 0 && (
                                <div className="text-center py-12 text-gray-500">
                                    <Users className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                                    <p>No personnel records found</p>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Compliance Tab */}
                {activeTab === 'compliance' && complianceSummary && (
                    <div className="bg-white rounded-lg shadow">
                        <div className="p-4 border-b">
                            <h3 className="text-lg font-semibold">Personnel Compliance Summary</h3>
                        </div>
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead className="bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    <tr>
                                        <th className="px-6 py-3">Staff Member</th>
                                        <th className="px-6 py-3">Role</th>
                                        <th className="px-6 py-3">CV</th>
                                        <th className="px-6 py-3">Job Desc</th>
                                        <th className="px-6 py-3">Confidentiality</th>
                                        <th className="px-6 py-3">GCP</th>
                                        <th className="px-6 py-3">Score</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200">
                                    {complianceSummary.data?.map(person => (
                                        <tr key={person.user_id} className="hover:bg-gray-50">
                                            <td className="px-6 py-4 font-medium">{person.staff_name}</td>
                                            <td className="px-6 py-4 text-sm text-gray-500">{person.functional_role || '-'}</td>
                                            <td className="px-6 py-4">
                                                {person.cv_status === 'Complete' 
                                                    ? <CheckCircle className="w-5 h-5 text-green-500" />
                                                    : <XCircle className="w-5 h-5 text-red-500" />}
                                            </td>
                                            <td className="px-6 py-4">
                                                {person.job_description_status === 'Complete' 
                                                    ? <CheckCircle className="w-5 h-5 text-green-500" />
                                                    : <XCircle className="w-5 h-5 text-red-500" />}
                                            </td>
                                            <td className="px-6 py-4">
                                                {person.confidentiality_status === 'Complete' 
                                                    ? <CheckCircle className="w-5 h-5 text-green-500" />
                                                    : <XCircle className="w-5 h-5 text-red-500" />}
                                            </td>
                                            <td className="px-6 py-4">
                                                {person.gcp_status === 'Current' 
                                                    ? <CheckCircle className="w-5 h-5 text-green-500" />
                                                    : person.gcp_status === 'Expired'
                                                        ? <AlertTriangle className="w-5 h-5 text-yellow-500" />
                                                        : <XCircle className="w-5 h-5 text-red-500" />}
                                            </td>
                                            <td className="px-6 py-4">
                                                <span className={`px-2 py-1 rounded-full text-sm font-bold ${getScoreColor(person.compliance_score)}`}>
                                                    {person.compliance_score}%
                                                </span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {/* Expiring Credentials Tab */}
                {activeTab === 'credentials' && (
                    <div className="bg-white rounded-lg shadow">
                        <div className="p-4 border-b">
                            <h3 className="text-lg font-semibold">Expiring Credentials (Next 90 Days)</h3>
                        </div>
                        {expiringCredentials.length > 0 ? (
                            <div className="overflow-x-auto">
                                <table className="w-full">
                                    <thead className="bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        <tr>
                                            <th className="px-6 py-3">Staff Member</th>
                                            <th className="px-6 py-3">Credential</th>
                                            <th className="px-6 py-3">Issuing Body</th>
                                            <th className="px-6 py-3">Expiration</th>
                                            <th className="px-6 py-3">Urgency</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-200">
                                        {expiringCredentials.map(cred => (
                                            <tr key={cred.credential_id} className="hover:bg-gray-50">
                                                <td className="px-6 py-4">
                                                    <p className="font-medium">{cred.staff_name}</p>
                                                    <p className="text-sm text-gray-500">{cred.email}</p>
                                                </td>
                                                <td className="px-6 py-4">
                                                    <p className="font-medium">{cred.credential_name}</p>
                                                    <p className="text-sm text-gray-500">{cred.credential_type}</p>
                                                </td>
                                                <td className="px-6 py-4 text-sm text-gray-500">{cred.issuing_body}</td>
                                                <td className="px-6 py-4">
                                                    <p className="font-medium">{new Date(cred.expiration_date).toLocaleDateString()}</p>
                                                    <p className="text-sm text-gray-500">{cred.days_until_expiry} days</p>
                                                </td>
                                                <td className="px-6 py-4">
                                                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                                        cred.urgency_level === 'EXPIRED' ? 'bg-red-100 text-red-800' :
                                                        cred.urgency_level === 'CRITICAL' ? 'bg-red-100 text-red-800' :
                                                        cred.urgency_level === 'WARNING' ? 'bg-yellow-100 text-yellow-800' :
                                                        'bg-blue-100 text-blue-800'
                                                    }`}>
                                                        {cred.urgency_level}
                                                    </span>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <div className="text-center py-12 text-gray-500">
                                <CheckCircle className="w-12 h-12 mx-auto mb-4 text-green-300" />
                                <p>No credentials expiring in the next 90 days</p>
                            </div>
                        )}
                    </div>
                )}
            </main>
        </div>
    );
};

export default PersonnelDirectory;
